/* global Rx, Redux, ReduxObservable, devToolsExtension, virtualDom, _ */

const { createStore, applyMiddleware, compose } = Redux;
const { createEpicMiddleware, combineEpics } = ReduxObservable;
import { ACTIONS } from "./_actions";
import {
	stateInit,
	getOptionsMatrix,
	getDefaultOption,
	getFile,
	GTMLogger,
	getOptionsDetails,
	getEnvironment,
	processIncompatibilities,
	convertName,
	getRegionDir,
	optionIsIncompatible
} from "./_utilities";

// import this way so that it can be removed from production
// since webpack doesn't tree shake this out otherwise
const createLogger = (process.env.NODE_ENV !== 'production')
	? require("redux-logger").createLogger
	: store => next => action => next(action);
const h = virtualDom.h;
const { culture = 'en-us', regionId = '1', truckModel = 'P4', matrixType = 'Cascadia_NorthAmerica' } = window;
const rootURL = getEnvironment() === "DEV"
	? "//freightlinertrucks-dev.azurewebsites.net/umbraco/Api"
	: "/umbraco/Api";

export function getRules(){
	return getFile(`/static/configurator/rules/${matrixType}.json`);
}

const loadConfiguratorEpic = (action$, store) => action$.ofType(ACTIONS.INIT_CONFIGURATOR)
	.flatMap(() => {
		let optionsPath = `${rootURL}/ConfiguratorOptions?matrixType=${matrixType}`;
		return Rx.Observable.zip(
			Rx.Observable.defer(() => getOptionsMatrix()).retry(2),
			Rx.Observable.defer(() => getFile(`/static/configurator/maps/${matrixType}-interior.json`)).retry(2),
			Rx.Observable.defer(() => getFile(`/static/configurator/maps/${matrixType}-exterior.json`)).retry(2),
			// previously deepLink, now state init.
			Rx.Observable.of(stateInit(store.getState())),
			Rx.Observable.defer(() => getFile(optionsPath)).retry(2),
		)
			.do(response => {
				//console.log(response);
				$("#configurator").removeClass("loading");
			})
			.map(response => {
				return {
					type: ACTIONS.INIT_CONFIGURATOR_FULFILLED,
					data: response
				};
			})
			.catch(error => {
				console.log("failed to init data:", error);
				return Rx.Observable.of({
					type: ACTIONS.INIT_CONFIGURATOR_FAILED,
					error
				});
			});
	});

const initConfiguratorFulfilledEpic = (action$, store) => action$.ofType(ACTIONS.INIT_CONFIGURATOR_FULFILLED)
	.do(() => {
		window.history.pushState({
			type: ACTIONS.CAB_RESET
		}, `Get Started`, ``);
	})
	// if global configuration injected by server based on url hash is present then use it to init settings
	.filter(() => configuration)
	.do(() => {
		$("#configurator").addClass("open");
	})
	.map(() => ({
		type: ACTIONS.CAB_CHANGE,
		data: {
			cabType: configuration.cabType,
			truckCab: configuration.truckCab
		}
	}));

const cabChangeEpic = (action$, store) => action$.ofType(ACTIONS.CAB_CHANGE)
	.do(() => {
		window.history.pushState({
			type: ACTIONS.VIEW_OPTION_CHANGE,
			data: "Exterior"
		}, `Exterior`, ``);
	})
	// if global configuration injected by server based on url hash is present then use it to init settings
	.filter(() => configuration)
	.do(action => {
		configuration = null;
	})
	.ignoreElements();

const configuratorEpics = combineEpics(
	loadConfiguratorEpic,
	initConfiguratorFulfilledEpic,
	cabChangeEpic
);

const epicMiddleware = createEpicMiddleware(configuratorEpics);

export const deepLinkMap = {
	"p4-126_bbc_day_cab": {
		truckModel: "P4",
		cabType: "day",
		truckCab: "126\" BBC Day Cab"
	},
	"p4-116_bbc_day_cab": {
		truckModel: "P4",
		cabType: "day",
		truckCab: "116\" BBC Day Cab"
	},
	"p4-116_bbc_48_xt": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "116\" BBC 48\" XT"
	},
	"p4-116_bbc_60_xt": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "116\" BBC 60\" XT"
	},
	"p4-116_bbc_60_rr": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "116\" BBC 60\" RR"
	},
	"p4-116_bbc_72_xt": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "116\" BBC 72\" XT"
	},
	"p4-116_bbc_72_rr": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "116\" BBC 72\" RR"
	},
	"p4-126_bbc_48_xt": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "126\" BBC 48\" XT"
	},
	"p4-126_bbc_60_xt": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "126\" BBC 60\" XT"
	},
	"p4-126_bbc_60_rr": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "126\" BBC 60\" RR"
	},
	"p4-126_bbc_72_xt": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "126\" BBC 72\" XT"
	},
	"p4-126_bbc_72_rr": {
		truckModel: "P4",
		cabType: "sleeper",
		truckCab: "126\" BBC 72\" RR"
	},
	"m2_106-106_day_cab": {
		truckModel: "M2 106",
		cabType: "day",
		truckCab: "106\" Day Cab"
	},
	"m2_106_plus-106_day_cab": {
		truckModel: "M2 106 Plus",
		cabType: "day",
		truckCab: "106\" Day Cab"
	}
};

const initState = {
	"standalone": false,
	"loading": false, // true, false or number 0 - 100
	"defaultTruck": "p4-126_bbc_day_cab",
	"truckModel": window.truckModel || "P4",
	"cabType": "",
	"truckCab": "",
	"truckCabs": window.cabTypes || 
		[
			{
				"key": "day",
				"label": "Day Cab",
				"cabs": [
					"116\" BBC Day Cab",
					"126\" BBC Day Cab"
				]
			},
			{
				"key": "sleeper",
				"label": "Sleeper Cab",
				"cabs": [
					"116\" BBC 48\" XT",
					"116\" BBC 60\" XT",
					"116\" BBC 60\" RR",
					"116\" BBC 72\" XT",
					"116\" BBC 72\" RR",
					"126\" BBC 48\" XT",
					"126\" BBC 60\" XT",
					"126\" BBC 60\" RR",
					"126\" BBC 72\" XT",
					"126\" BBC 72\" RR"
				]
			}
		],
		// "day": [
		// 	"116\" BBC Day Cab",
		// 	"126\" BBC Day Cab"
		// ],
		// "sleeper": [
		// 	"116\" BBC 48\" XT",
		// 	"116\" BBC 60\" XT",
		// 	"116\" BBC 60\" RR",
		// 	"116\" BBC 72\" XT",
		// 	"116\" BBC 72\" RR",
		// 	"126\" BBC 48\" XT",
		// 	"126\" BBC 60\" XT",
		// 	"126\" BBC 60\" RR",
		// 	"126\" BBC 72\" XT",
		// 	"126\" BBC 72\" RR"
		// ]
	"interiorViewRadius": 450,
	"hotspotAngle": 315,
	"hotspotScaleWidth": 1365,
	"hotspotScaleHeight": 910,
	"socialShareAngle": 315,
	"nav": "build",
	"navs": [
		"explore",
		"build"
	],
	"modes": {
		interior: "Interior",
		exterior: "Exterior",
		misc: "Powertrain + Safety + Connectivity"
	},
	"view": "exterior",
	"views": [
		"exterior",
		"interior"
	],
	"activeOption": undefined,
	"optionsInView": [],
	"focusedOption": undefined,
	"optionInteracted": {},
	"pillar": "Fuel Efficiency",
	"pillars": [
		{
			id: "fuel-efficiency",
			name: "Fuel Efficiency",
			icon: "icon-Fuel-Efficiency"
		},
		{
			id: "connectivity",
			name: "Connectivity",
			icon: "icon-connectivity"
		},
		{
			id: "safety",
			name: "Safety",
			icon: "icon-safety"
		},
		{
			id: "quality",
			name: "Quality",
			icon: "icon-Quality"
		},
		{
			id: "uptime",
			name: "Uptime",
			icon: "icon-Uptime"
		},
		{
			id: "driver-experience",
			name: "Driver Experience",
			icon: "icon-driver-experience"
		}
	],
	"viewOptions": [
		{
			id: "exterior",
			name: "Exterior"
		},
		{
			id: "interior",
			name: "Interior"
		},
		{
			id: "misc",
			name: "Powertrain & Safety"
		}
	],
	"viewOption": "Exterior",
	"previousViewOption": "",
	"interiorViews": [
		"p4-116_bbc_day_cab",
		"p4-126_bbc_day_cab",
		"p4-116_bbc_60_rr",
		"p4-126_bbc_60_rr",
		"p4-116_bbc_72_rr",
		"p4-126_bbc_72_rr",
		"p4-116_bbc_48_xt",
		"p4-126_bbc_48_xt",
		"p4-116_bbc_60_xt",
		"p4-126_bbc_60_xt",
		"p4-116_bbc_72_xt",
		"p4-126_bbc_72_xt",
		"m2_106-106_day_cab",
		"m2_106_plus-106_day_cab"
	],
	"settingsBackup": {},
	"interiorBuildSettings": {},
	"interiorBuildChange": {},
	"exteriorBuildSettings": {},
	"exteriorBuildChange": {},

	"altWorkflows": {
		"Powertrain Package": "Powertrain Package",
		"Paquete de Tren Motriz": "Powertrain Package",
		"Safety": "Safety Systems",
		"Seguridad": "Safety Systems"
	},

	"alwaysGrouped": [
		'Seat Upholstery',
		'Trim'
	],

	// "miscOptions": [
	// 	"Powertrain Package",
	// 	"Engine",
	// 	"Transmission",
	// 	"Front Axle",
	// 	"Rear Axle",
	// 	"Rear Axle Ratios",
	// 	"Axle Lubrication Management",
	// 	"Intelligent Powertrain Management",
	// 	"Front Suspension",
	// 	"Rear Suspension",
	// 	"Safety Systems",
	// 	"Collision Mitigation System",
	// 	"Lane Departure Warning",
	// 	"Side Object Detection",
	// 	"Detroit Connect",
	// 	"Zonar"
	// ],
	// "miscBuildSettings": {},
	// "miscCategoryRules": [
	// 	"Transmission",
	// 	"Front Axle",
	// 	"Rear Axle",
	// 	"Front Suspension",
	// 	"Rear Suspension"
	// ],
	"activeOptionChildren": [],

	"optionDetails": undefined,
	"activeDetails": undefined,
	"summaryActive": false,
	"activeFab": false,
	"clearActiveFab": false,
	"dataReady": false,
	"childrenActive": false,
	"quoteDisabled": false,
};

initState.flatCabs = initState.truckCabs.reduce(
	(prev, cat) => prev.concat(cat.cabs), []).map(cab => initState.truckModel + ' ' + cab);

function configurator(state = initState, action) {
	
	let tempState,
		truckModel,
		cabType,
		truckCab,
		groupedOptionsMatrix;
	switch (action.type) {
		case ACTIONS.SET_LOADING:
			return Object.assign({}, state, {
				loading: action.data,
				actionType: "set_loading"
			});
		case ACTIONS.INIT_CONFIGURATOR_FULFILLED:
			truckModel = deepLinkMap[action.data[3].truck] ?
				deepLinkMap[action.data[3].truck].truckModel : state.truckModel;

			cabType = deepLinkMap[action.data[3].truck] ?
				deepLinkMap[action.data[3].truck].cabType : state.cabType;

			truckCab = deepLinkMap[action.data[3].truck] ?
				deepLinkMap[action.data[3].truck].truckCab : state.truckCab;

			groupedOptionsMatrix = _.chain(action.data[0])			
				.mapValues(mode => {
					return _.chain(mode)
						.map(option => {
							return option;
						})
						.flatten()
						.filter(option => state.flatCabs.reduce((found, cab) => found || !!option[cab], false))
						.groupBy(option => option["Grouping (Web)"])
						.mapValues(group => _.groupBy(group, "Component"))
						.mapValues(group => {
							let keys = Object.keys(group);
							if (keys.length > 1 || (keys.length === 1 && state.alwaysGrouped.indexOf(group[keys[0]][0].Component) !== -1)) {
								return group;
							}
							return group[keys[0]];
						})
						.value();
				}).value();
			
			tempState = Object.assign({}, state, {
				// update options matrix
				optionsMatrix: action.data[0],
				groupedOptionsMatrix: groupedOptionsMatrix,
				// update the interior file map
				interiorMap: action.data[1],
				// update the exterior file map
				exteriorMap: action.data[2],
				// update the truck, cab , view and standalone status from the url
				// if global configuration injected by server based on url hash is present then use it to init settings
				truckModel: configuration ? configuration.truckModel : truckModel,
				cabType: configuration ? configuration.cabType : cabType,
				truckCab: configuration ? configuration.truckCab : truckCab,
				view: action.data[3].view,
				pillar: action.data[3].pillar,
				viewOption: action.data[3].view === "interior" ? _.capitalize(action.data[3].view) : state.viewOption,
				nav: action.data[3].mode,
				standalone: action.data[3].standalone,
				optionDetails: action.data[4],
				// ready to render
				dataReady: true,
				loading: false,
				optionInteracted: configuration ? configuration.optionInteracted : {}
			});
			
			const retObj = Object.assign({}, tempState, {
				interiorBuildSettings: getBuildSettingsInit(tempState, state.modes.interior, null, null, configuration),
				exteriorBuildSettings: getBuildSettingsInit(tempState, state.modes.exterior, null, null, configuration),
				miscBuildSettings: getBuildSettingsInit(tempState, state.modes.misc, null, null, configuration),
			});

			return retObj;

		case ACTIONS.INIT_CONFIGURATOR_FAILED:
			return Object.assign({}, state, {
				error: action.error,
				init: "failed",
				loading: false
			});
		case ACTIONS.SET_OPTION_CHILDREN:
			return Object.assign({}, state, {
				activeOptionChildren: action.data,
				actionType: ACTIONS.SET_OPTION_CHILDREN
			});
		case ACTIONS.SET_CHILDREN_ACTIVE:
			return Object.assign({}, state, {
				childrenActive: action.data,
				actionType: ACTIONS.SET_CHILDREN_ACTIVE
			});
		case ACTIONS.ACTIVE_DETAILS_CHANGE:

			return Object.assign({}, state, {
				activeDetails: action.data ? getOptionsDetails(state.optionDetails, state.truckModel, state.truckCab, action.data) : undefined,
				actionType: ACTIONS.ACTIVE_DETAILS_CHANGE
			});
		case ACTIONS.BUILD_SETTINGS_BACKUP:
			let {interiorBuildSettings, exteriorBuildSettings, miscBuildSettings} = state;
			return Object.assign({}, state, {
				settingsBackup: {interiorBuildSettings:{...interiorBuildSettings}, exteriorBuildSettings: {...exteriorBuildSettings}, miscBuildSettings: {...miscBuildSettings}},
				activeOptionChildren: [],
				actionType: ACTIONS.BUILD_SETTINGS_BACKUP
			});
		case ACTIONS.BUILD_SETTINGS_RESTORE:
			let {settingsBackup} = state;
			if (settingsBackup && Object.keys(settingsBackup).length > 0) {
				return Object.assign({}, state, {
					...settingsBackup, 
					settingsBackup: {},
					activeOptionChildren: [],
					childrenActive: false,
					actionType: ACTIONS.BUILD_SETTINGS_RESTORE
				});
			}
		case ACTIONS.BUILD_SETTINGS_BACKUP_CLEAR:
			return Object.assign({}, state, {
				settingsBackup: {},
				activeOptionChildren: [],
				childrenActive: false,
				actionType: ACTIONS.BUILD_SETTINGS_BACKUP_CLEAR
			});
		case ACTIONS.INTERIOR_BUILD_CHANGE:
			//console.log('EXT BUILD',action.exteriorBuildSettings);
			return Object.assign({}, state, {
				interiorBuildSettings: Object.assign({}, state.interiorBuildSettings, action.buildSettings),
				interiorBuildChange: Object.assign({}, action.buildSettings),
				exteriorBuildSettings: Object.assign({}, state.exteriorBuildSettings, action.exteriorBuildSettings),
				exteriorBuildChange: Object.assign({}, action.exteriorBuildSettings),
				actionType: ACTIONS.INTERIOR_BUILD_CHANGE
			});
		case ACTIONS.EXTERIOR_BUILD_CHANGE:
			// Pull in default settings for options that where false.
			for( var key in action.buildSettings ){
				let defaultValue = getBuildSettingsDefault(state, state.modes.exterior, null, null, configuration)[key],
					incompatible = _.chain(state.optionsMatrix.Exterior[key])
						.filter(option => option.Key === defaultValue)
						.some(option => optionIsIncompatible(option, action.buildSettings))
						.value();
				if(!action.buildSettings[key]){
					action.buildSettings[key] = !incompatible ? defaultValue : false;	
				}
			}
			return Object.assign({}, state, {
				exteriorBuildSettings: Object.assign({}, state.exteriorBuildSettings, action.buildSettings),
				exteriorBuildChange: Object.assign({}, action.buildSettings),
				actionType: ACTIONS.EXTERIOR_BUILD_CHANGE
			});
		case ACTIONS.POWERTRAIN_BUILD_CHANGE:
			return Object.assign({}, state, {
				powertrainBuildSettings: Object.assign({}, state.powertrainBuildSettings, action.buildSettings),
				interiorBuildChange: {},
				exteriorBuildChange: {},
				actionType: ACTIONS.POWERTRAIN_BUILD_CHANGE
			});
		case ACTIONS.MISC_BUILD_CHANGE:
			return Object.assign({}, state, {
				miscBuildSettings: Object.assign({}, state.miscBuildSettings, action.buildSettings),
				interiorBuildChange: {},
				exteriorBuildChange: {},
				actionType: ACTIONS.MISC_BUILD_CHANGE
			});
		case ACTIONS.SAFETY_BUILD_CHANGE:
			return Object.assign({}, state, {
				safetyBuildSettings: Object.assign({}, state.safetyBuildSettings, action.buildSettings),
				interiorBuildChange: {},
				exteriorBuildChange: {},
				actionType: ACTIONS.SAFETY_BUILD_CHANGE
			});
		case ACTIONS.INTERIOR_BUILD_SETTINGS_INIT:
			return Object.assign({}, state, {
				interiorBuildSettings: action.data
			});
		case ACTIONS.EXTERIOR_BUILD_SETTINGS_INIT:
			return Object.assign({}, state, {
				exteriorBuildSettings: action.data
			});
		case ACTIONS.MAIN_NAV_CHANGE:
			return Object.assign({}, state, {
				nav: action.data,
				activeFab: false,
				clearActiveFab: false,
				interiorBuildChange: {},
				exteriorBuildChange: {},
				actionType: ACTIONS.MAIN_NAV_CHANGE
			});
		case ACTIONS.VIEW_CHANGE:
			return Object.assign({}, state, {
				view: action.data,
				viewOption: _.capitalize(action.data),
				activeFab: false,
				clearActiveFab: false,
				actionType: ACTIONS.VIEW_CHANGE
			});
		case ACTIONS.ACTIVE_OPTION_CHANGE:			
			return Object.assign({}, state, {
				activeOption: action.data,
				actionType: ACTIONS.ACTIVE_OPTION_CHANGE
			});
		case ACTIONS.OPTIONS_IN_VIEW_CHANGE:
			return Object.assign({}, state, {
				optionsInView: action.data,
				actionType: ACTIONS.OPTIONS_IN_VIEW_CHANGE
			});
		case ACTIONS.OPTIONS_IN_VIEW_SELECTION_CHANGE:
			return Object.assign({}, state, {
				optionsInView: action.data,
				actionType: ACTIONS.OPTIONS_IN_VIEW_SELECTION_CHANGE
			});
		case ACTIONS.FOCUSED_OPTION_CHANGE:
			return Object.assign({}, state, {
				focusedOption: action.data,
				actionType: ACTIONS.FOCUSED_OPTION_CHANGE
			});
		case ACTIONS.OPTION_INTERACTED:
			let newOptionInteracted = {};
			newOptionInteracted[state.activeOption] = true;
			return Object.assign({}, state, {
				optionInteracted: Object.assign({}, state.optionInteracted, newOptionInteracted),
				actionType: ACTIONS.OPTION_INTERACTED
			});
		case ACTIONS.PILLAR_CHANGE:
			return Object.assign({}, state, {
				pillar: action.data,
				activeFab: false,
				clearActiveFab: false,
				exteriorBuildChange: Object.assign({}, {
					"Pillar": action.data
				}),
				interiorBuildChange: Object.assign({}, {
					"Pillar": action.data
				}),
				actionType: ACTIONS.PILLAR_CHANGE
			});
		case ACTIONS.VIEW_OPTION_CHANGE:
			return Object.assign({}, state, {
				view: action.data.toLowerCase().replace(/\s/gi, ''),
				previousViewOption: state.viewOption,
				viewOption: action.data,
				activeFab: false,
				clearActiveFab: false,
				exteriorBuildChange: {},
				interiorBuildChange: {},
				actionType: ACTIONS.VIEW_OPTION_CHANGE
			});
		case ACTIONS.ACTIVE_SUMMARY_CHANGE:
			return Object.assign({}, state, {
				summaryActive: action.data,
				activeOption: undefined,
				actionType: ACTIONS.ACTIVE_SUMMARY_CHANGE
			});
		case ACTIONS.ACTIVE_FAB_CHANGE:
			return Object.assign({}, state, {
				activeFab: action.data,
				clearActiveFab: false,
				actionType: ACTIONS.ACTIVE_FAB_CHANGE
			});
		case ACTIONS.ACTIVE_FAB_CLEAR:
			return Object.assign({}, state, {
				activeFab: false,
				clearActiveFab: true,
				actionType: ACTIONS.ACTIVE_FAB_CLEAR
			});
		case ACTIONS.CAB_TYPE_CHANGE:
			return Object.assign({}, state, {
				cabType: action.data.cabType,
				actionType: ACTIONS.CAB_TYPE_CHANGE
			});
		case ACTIONS.CAB_CHANGE:
			tempState = Object.assign({}, state, {
				cabType: action.data.cabType,
				truckCab: action.data.truckCab,
				actionType: ACTIONS.CAB_CHANGE
			});
			
			if (window.tellParent) {
				window.tellParent.maximize();
			}
			
			return Object.assign({}, tempState, {
				interiorBuildSettings: getBuildSettingsInit(tempState, state.modes.interior, null, null, configuration),
				exteriorBuildSettings: getBuildSettingsInit(tempState, state.modes.exterior, null, null, configuration),
				miscBuildSettings: getBuildSettingsInit(tempState, state.modes.misc, null, null, configuration),
			});
		case ACTIONS.CAB_RESET:
			if (window.tellParent) {
				window.tellParent.restore();
			}
			return Object.assign({}, state, {
				cabType: "",
				truckCab: "",
				activeOption: undefined,
				optionInteracted: {},
				activeOptionChildren: [],
				activeFab: false,
				clearActiveFab: false,
				childrenActive: false,
				summaryActive: false,
				nav: "explore",
				view: "exterior",
				viewOption: "Exterior",
				pillar: "Fuel Efficiency",
				exteriorBuildChange: {},
				interiorBuildChange: {},
				actionType: ACTIONS.CAB_RESET
			});
		case ACTIONS.RESIZE_EXTERIOR:
			return Object.assign({}, state, {
				actionType: ACTIONS.RESIZE_EXTERIOR
			});
		case ACTIONS.QUOTE_DISABLED:
			return Object.assign({}, state, {
				actionType: ACTIONS.QUOTE_DISABLED,
				quoteDisabled: action.data
			});
		case ACTIONS.HACK_COLORS: {
			let newColors = Object.assign({}, state.optionsMatrix.Exterior.Color);
			newColors[""].push({
				"Mode": "Exterior",
				"Component": "Color",
				"Category": "",
				"Option": action.colorName,
				"Key": `color_${action.colorName.replace(/\s+/g, "")}`,
				"Data": action.color,
				"Incompatibilities": "",
				"P4 116\" BBC Day Cab\"": "X",
				"P4 126\" BBC Day Cab\"": "X",
				"P4 116\" BBC 48\" XT": "X",
				"P4 126\" BBC 48\" XT": "X",
				"P4 116\" BBC 60\" XT": "X",
				"P4 126\" BBC 60\" XT": "X",
				"P4 116\" BBC 60\" RR": "X",
				"P4 126\" BBC 60\" RR": "X",
				"P4 116\" BBC 72\" XT": "X",
				"P4 126\" BBC 72\" XT": "X",
				"P4 116\" BBC 72\" RR": "X",
				"P4 126\" BBC 72\" RR": "X",
				"M2 106 106\" BBC Day Cab": "X",
				"M2 106 Plus 106\" BBC Day Cab": "X",
			});
			return Object.assign({}, state, {
				optionsMatrix: Object.assign({}, state.optionsMatrix, {
					Exterior: Object.assign({}, state.optionsMatrix.Exterior, {
						Color: newColors
					})
				})
			});
		}
		default:
			return state;
	}
}

/**
 * Take the path tree Nodes format and convert it to named properties object format
 * @param obj - path tree data
 * @param cb - formatting callback
 * @returns {*} - formatted object
 */
function formatPathTree(obj, cb) {
	if (_.isUndefined(obj)) {
		throw new Error(`map-keys-deep-lodash expects an object but got ${typeof obj}`);
	}

	if (obj.nodes) {
		obj = _.mapKeys(obj, cb);
	} else if (_.isArray(obj)) {
		obj = _.mapKeys(obj, cb);
	} else {
		return {
			id: obj.id
		};
	}

	let res = {};

	for (const key in obj) {
		if (obj.hasOwnProperty(key)) {
			const val = obj[key];
			if (_.isArray(val)) {
				res = formatPathTree(val, cb);
			} else if (_.isObject(val)) {
				res[key] = formatPathTree(val, cb);
			} else {
				res.id = obj.id;
			}
		}
	}

	return res;
}

function getBuildSettingsDefault(state, view, filter, rules, configuration) {
	let options = state.optionsMatrix[view];

	if (view === state.modes.exterior) {
		options.Cab = state.optionsMatrix.Cab.Cab;
	}

	if (filter) {
		options = _.pick(options, state[filter]);
	}

	let buildSettings = _.chain(options).mapValues((component, componentKey) => {
		if (componentKey === "Cab") {
			return "cab";
		}
		// if global configuration injected by server based on url hash is present then use it to init settings
		if (configuration && configuration[componentKey]) {
			return configuration[componentKey];
		}
		
		return getDefaultOption(state, component, state[rules]);
	}).value();

	return buildSettings;

}

function getBuildSettingsInit(state, view, filter, rules, configuration) {
	return processIncompatibilities({
		buildSettings: getBuildSettingsDefault(state, view, filter, rules, configuration), 
		state, 
		components: state.optionsMatrix.Exterior
	});
}

const middleware = [epicMiddleware];
if (process.env.NODE_ENV === `development`) {
	middleware.push(createLogger({
		collapsed: true
	}));
}

export const store = createStore(configurator,
	compose(
		applyMiddleware(
			...middleware,
			GTMLogger
		),
		window.devToolsExtension && process.env.NODE_ENV === `development` ? window.devToolsExtension() : (f => f)
	));
export const store$ = Rx.Observable.from(store);
